import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "rentmore24" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Workspace created. Demo data loaded.");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-sand-100">
      <div
        className="hidden md:block relative grain-bg"
        style={{
          backgroundImage:
            "linear-gradient(rgba(27,67,50,0.55), rgba(27,67,50,0.25)), url(https://images.unsplash.com/photo-1776090974926-0ffca9a8177e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBleHRlcmlvciUyMGRheWxpZ2h0fGVufDB8fHx8MTc3Njk3OTY5M3ww&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <Logo />
          <div className="max-w-md text-white">
            <div className="uppercase tracking-[0.25em] text-xs text-sand-200 mb-4">Get started in 60 seconds</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight">
              Your leasing team's new favorite AI co-pilot.
            </h1>
            <p className="mt-5 text-sand-100/90">
              Sign up and we'll populate your workspace with realistic demo leases, tenants, and emails so you can explore every feature immediately.
            </p>
          </div>
          <div className="text-xs text-sand-200">© 2026 rentmore24.com</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5" data-testid="signup-form">
          <div className="md:hidden mb-4"><Logo /></div>
          <div>
            <h2 className="font-heading text-3xl font-semibold">Create your workspace</h2>
            <p className="text-sm text-stone-500 mt-1">Demo data is seeded automatically.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="full_name">Full name</Label>
              <Input id="full_name" required value={form.full_name} onChange={onChange("full_name")} data-testid="signup-name-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={onChange("email")} data-testid="signup-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password (min 6)</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={onChange("password")} data-testid="signup-password-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={form.company} onChange={onChange("company")} data-testid="signup-company-input" className="mt-1" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-forest-600 hover:bg-forest-500 text-white h-11" data-testid="signup-submit-button">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-stone-500 text-center">
            Have an account?{" "}
            <Link to="/login" className="text-forest-600 font-medium hover:underline" data-testid="login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
